import React, { useState } from 'react';
import { Phone, Mail, MapPin, Leaf, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { companyInfo } from '../data/mockData';
import { Button } from './ui/button';
import { BookingModal } from './BookingModal';

export const Footer = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <footer className="bg-gray-900 text-white pt-20 pb-8">
        <div className="container mx-auto px-4">
          {/* Top CTA Section */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-3xl p-12 mb-16 text-center">
            <h3 className="text-4xl font-bold mb-4">Ready to Get Started?</h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Get your free estimate today and experience the Next Generation difference in tree care and landscaping.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone.replace(/\D/g, '')}`}>
                <Button className="bg-white text-green-600 hover:bg-gray-100 font-bold text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call {companyInfo.phone}
                </Button>
              </a>
              <Button
                onClick={() => setIsBookingOpen(true)}
                className="bg-black hover:bg-gray-800 text-white font-bold text-lg px-8 py-6 border-2 border-white"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Online
              </Button>
            </div>
          </div>

          {/* Footer Content */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_premium-arborcare/artifacts/u250ehvi_print%202%20af.jpg"
                  alt="Next Generation Tree Service & Landscaping"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h3 className="font-bold text-lg text-white">Next Generation</h3>
                  <p className="text-green-400 text-sm">Tree Service & Landscaping</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Professional tree care and landscaping services serving Nashville and surrounding areas. Family-owned and operated with over 10 years of experience.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-bold text-xl mb-6">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => scrollToSection('services')}
                    className="text-gray-400 hover:text-green-400 transition-colors"
                  >
                    Our Services
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('about')}
                    className="text-gray-400 hover:text-green-400 transition-colors"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('gallery')}
                    className="text-gray-400 hover:text-green-400 transition-colors"
                  >
                    Gallery
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('reviews')}
                    className="text-gray-400 hover:text-green-400 transition-colors"
                  >
                    Reviews
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="text-gray-400 hover:text-green-400 transition-colors"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-bold text-xl mb-6">Services</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="hover:text-green-400 transition-colors cursor-pointer">Tree Removal</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Tree Trimming</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Emergency Services</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Stump Grinding</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Landscaping</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Lawn Care</li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-bold text-xl mb-6">Contact Us</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <a
                      href={`tel:${companyInfo.phone.replace(/\D/g, '')}`}
                      className="text-gray-400 hover:text-green-400 transition-colors block"
                    >
                      {companyInfo.phone}
                    </a>
                    <a
                      href={`tel:${companyInfo.phoneSecondary.replace(/\D/g, '')}`}
                      className="text-gray-400 hover:text-green-400 transition-colors block"
                    >
                      {companyInfo.phoneSecondary}
                    </a>
                    <p className="text-sm text-gray-500">Available 24/7</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-400">{companyInfo.location}</p>
                    <p className="text-sm text-gray-500">& Surrounding Areas</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <a
                    href="mailto:nextgenerationnash@gmail.com"
                    className="text-gray-400 hover:text-green-400 transition-colors"
                  >
                    nextgenerationnash@gmail.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8 mt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
              </p>
              <div className="flex space-x-6 text-sm text-gray-400">
                <button
                  onClick={() => navigate('/privacy-policy')}
                  className="hover:text-green-400 transition-colors"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => navigate('/terms-of-service')}
                  className="hover:text-green-400 transition-colors"
                >
                  Terms of Service
                </button>
              </div>
            </div>
            <p className="text-gray-500 text-sm text-center mt-4">
              Licensed, Insured & Bonded • Certified Arborists • Serving Nashville Since 2013
            </p>
          </div>
        </div>
      </footer>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </>
  );
};
